import { motion } from 'motion/react';

/* ============================================================================
 * dmsTheme — shared GRN-derived design system for the 3 DMS distributor pages
 * (DMSUprisingInventory, FatehpuriaInventory, ChoudharyInventory).
 *
 * Extracted/generalized from frontend/src/pages/GRNManagement.jsx: the
 * --grn-* token set, Space Grotesk font, FluidStatusTabs sliding-pill nav,
 * bento KPI cards with a spring Counter, and the dark beam header. Scope,
 * per the user: adopt GRN's tokens/components, NOT its persistent 2-column
 * split-panel layout — these pages stay normal single-column tab-switching.
 * ========================================================================== */

export const EMBEDDED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;800&display=swap');

:root {
  --grn-surface: #fff; --grn-surface-subtle: #FBFAFD;
  --grn-border: #ECE7F2; --grn-border-soft: #E4DEEE;
  --grn-text: #231C30; --grn-text-muted: #8C82A0; --grn-text-faint: #9A90AE;
  --grn-accent-surface: #231C30;
  --grn-purple: #6E58A6; --grn-purple-tint: #EEE9F8;
  --grn-amber-tint: #F5EDDC; --grn-amber: #9A7B3E; --grn-amber-border: #EADFC5;
  --grn-green-tint: #E7F0E8; --grn-green: #5C7E60; --grn-green-border: #D6E6D8;
  --grn-blue-tint: #DCEAF5; --grn-blue: #3A6B8A;
  --grn-pink-tint: #F7E5EA; --grn-error: #C25E77;
  --grn-page-bg: transparent;
  --grn-shadow-sm: 0 1px 2px rgba(35,28,48,0.05);
  --grn-shadow-md: 0 6px 20px rgba(35,28,48,0.10);
  --grn-shadow-lg: 0 20px 50px rgba(35,28,48,0.22);
}

.dms-root { font-family: 'Space Grotesk', sans-serif; }
.dms-tab:hover { opacity: 0.85; }
.dms-tab { background: none; }

.dms-beam-bg { position: absolute; inset: 0; display: flex; overflow: hidden; pointer-events: none; }
.dms-beam-lane { flex: 1; position: relative; }
.dms-beam {
  position: absolute; top: -40px; left: 50%; height: var(--length); background: linear-gradient(to bottom, transparent, #fff, transparent);
  opacity: var(--opacity); animation: dms-beam-fall var(--duration) linear var(--delay) infinite;
}
@keyframes dms-beam-fall {
  0% { transform: translate(-50%, -10%); }
  100% { transform: translate(-50%, 420px); }
}

.dms-spinner { animation: dms-spin 0.8s linear infinite; }
@keyframes dms-spin { to { transform: rotate(360deg); } }
`;

export const ICON = { xs: 12, sm: 14, md: 16, lg: 20 };
export const SHADOW_SM = 'var(--grn-shadow-sm)';
export const SHADOW_MD = 'var(--grn-shadow-md)';
export const SHADOW_LG = 'var(--grn-shadow-lg)';
export const FONT = "'Space Grotesk', sans-serif";

/* ---------------------------------------------------------------------- */
/* Counter — renders the value instantly (no count-up animation).          */
/* ---------------------------------------------------------------------- */
export function Counter({ value, style, prefix = '' }) {
  return <span style={style}>{prefix}{Math.round(value || 0).toLocaleString('en-IN')}</span>;
}

/* ---------------------------------------------------------------------- */
/* FluidStatusTabs — shared layoutId pill, spring-slides between tabs.      */
/* Generalized beyond GRN's hardcoded status list: takes `tabs` as          */
/* [{ key, label, emoji? }].                                                */
/* ---------------------------------------------------------------------- */
export function FluidStatusTabs({ tabs, value, onChange, layoutId = 'dms-tab-pill' }) {
  return (
    <nav aria-label="Tab navigation" style={S.fluidNav}>
      <div role="tablist" style={S.fluidList}>
        {tabs.map((opt) => {
          const active = opt.key === value;
          return (
            <button
              key={opt.key} type="button" role="tab" aria-selected={active} className="dms-tab"
              onClick={() => onChange(opt.key)} style={S.fluidTab}
            >
              {active && (
                <motion.span
                  layoutId={layoutId} style={S.fluidPill}
                  transition={{ type: 'spring', stiffness: 380, damping: 34, mass: 0.75 }}
                />
              )}
              <span style={{ ...S.fluidTabContent, color: active ? '#fff' : 'var(--grn-text-muted)' }}>
                {opt.emoji && <span aria-hidden style={{ fontSize: 13, filter: 'grayscale(1)' }}>{opt.emoji}</span>}
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/* ---------------------------------------------------------------------- */
/* Bento KPI card — grid of stat tiles with Counter.                       */
/* ---------------------------------------------------------------------- */
export function KpiBento({ items, columns = 'repeat(auto-fit, minmax(200px, 1fr))' }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: columns, gap: 14 }}>
      {items.map((it) => (
        <div key={it.label} style={S.kpiCard}>
          <div style={S.kpiLabel}>{it.label}</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: it.color || 'var(--grn-text)' }}>
            {typeof it.value === 'number'
              ? <Counter value={it.value} prefix={it.prefix || ''} />
              : it.value}
          </div>
          {it.sub && <div style={{ fontSize: 11, color: 'var(--grn-text-muted)', marginTop: 4 }}>{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Header beam background — diagonal falling-streak meteor lanes, dark bar. */
/* ---------------------------------------------------------------------- */
const prand = (n) => { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };
const lerp = (min, max, t) => min + (max - min) * t;
const LANES = 36;

function Beam({ index }) {
  const fast = prand(index + 91) < 0.3;
  const duration = fast ? lerp(1.2, 3.2, prand(index)) : lerp(6, 14, prand(index));
  const delay = lerp(0, 9, prand(index + 13));
  const length = Math.round(lerp(10, 26, prand(index + 29)));
  const width = Math.max(2, Math.round(lerp(2, 4, prand(index + 53))));
  const opacity = fast ? lerp(0.32, 0.5, prand(index + 71)) : lerp(0.14, 0.32, prand(index + 71));
  return (
    <div className="dms-beam" style={{
      '--duration': `${duration.toFixed(2)}s`, '--delay': `${delay.toFixed(2)}s`,
      '--length': `${length}px`, '--opacity': opacity.toFixed(2), width: `${width}px`,
    }} />
  );
}

export function HeaderBeamBackground() {
  return (
    <div className="dms-beam-bg" aria-hidden="true">
      {Array.from({ length: LANES }, (_, i) => (
        <div key={i} className="dms-beam-lane">{prand(i + 7) < 0.3 && <Beam index={i + 1} />}</div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* DmsHeader — dark (#0B0A12) sticky header bar with beam background,      */
/* h1 fontWeight:300 + bold accent second word.                            */
/* ---------------------------------------------------------------------- */
export function DmsHeader({ title, accent, subtitle, right }) {
  return (
    <div style={S.header}>
      <HeaderBeamBackground />
      <div style={S.headerInner}>
        <div>
          <h1 style={S.h1}>
            {title}{' '}<span style={{ fontWeight: 700, color: 'var(--grn-purple-tint)' }}>{accent}</span>
          </h1>
          {subtitle && <p style={S.headerSubtitle}>{subtitle}</p>}
        </div>
        {right && <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>{right}</div>}
      </div>
    </div>
  );
}

const S = {
  header: { position: 'sticky', top: 0, zIndex: 20, background: '#0B0A12', overflow: 'hidden', padding: '18px 24px' },
  headerInner: { position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 },
  h1: { fontSize: 26, fontWeight: 300, color: '#fff', margin: 0, letterSpacing: '-0.01em' },
  headerSubtitle: { fontSize: 17, color: 'rgba(255,255,255,0.6)', marginTop: 6, marginBottom: 0 },
  fluidNav: { display: 'inline-block' },
  fluidList: { display: 'flex', gap: 2, background: 'var(--grn-surface-subtle)', borderRadius: 999, padding: 4, border: '1px solid var(--grn-border)' },
  fluidTab: { position: 'relative', border: 'none', cursor: 'pointer', padding: '9px 18px', borderRadius: 999, background: 'transparent', fontFamily: FONT },
  fluidPill: { position: 'absolute', inset: 0, background: 'var(--grn-accent-surface)', borderRadius: 999, zIndex: 0 },
  fluidTabContent: { position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap' },
  kpiCard: { background: 'var(--grn-surface)', borderRadius: 16, padding: '18px 20px', boxShadow: SHADOW_SM, border: '1px solid var(--grn-border)' },
  kpiLabel: { fontSize: 11, fontWeight: 700, color: 'var(--grn-text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 },
};

export const dmsStyles = S;
